import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { uploadFile } from "../../api/uploadFile";
import { formatDateTime } from "../../utils/format";
import ErrorModal from "../../components/ErrorModal";
import { usePermission } from "../../hooks/usePermission";

const POLL_INTERVAL_MS = 1000;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Voortgangsbalk + stage-tekst, gedeeld door beide importpaden (upload en
// auto-export) — pollt de job-status tot die 'done' of 'error' is.
function JobProgress({ job }) {
  if (!job) return null;
  const pct = job.total > 0 ? Math.round((job.current / job.total) * 100) : job.status === "done" ? 100 : 15;
  return (
    <div className="mt-4">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{job.stage}</span>
        {job.total > 0 && <span>{job.current} / {job.total}</span>}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all ${job.status === "error" ? "bg-red-500" : "bg-blue-600"}`}
          style={{ width: `${job.status === "running" ? Math.max(pct, 8) : 100}%` }}
        />
      </div>
    </div>
  );
}

function ImportResultSummary({ result }) {
  if (!result) return null;
  if (result.message && !result.imported) {
    return <div className="mt-3 text-sm text-gray-500">{result.message}</div>;
  }
  return (
    <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
      <div><strong>{result.imported}</strong> rijen geïmporteerd, {result.skipped_duplicates} dubbels overgeslagen.</div>
      {result.deleted_historisch > 0 && (
        <div>{result.deleted_historisch} historische schattingen vervangen door deze import.</div>
      )}
      {result.period_from && (
        <div className="text-emerald-700">Periode: {result.period_from} — {result.period_to}</div>
      )}
      {result.overig_bedrag > 0 && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-800">
          ⚠️ <strong>€ {result.overig_bedrag.toFixed(2)}</strong> ({result.overig_aantal} rijen) kon niet aan een
          specifieke categorie toegewezen worden en staat onder "Overig" — mogelijk een nieuw of gewijzigd
          consultatietype in Mediris. Controleer de Consultaties-lijst (filter op categorie "Overig").
        </div>
      )}
    </div>
  );
}

export default function MedirisImportPanel({ onClose, onImported }) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission("results.manage");

  const [tab, setTab] = useState("import");
  const [environment, setEnvironment] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [job, setJob] = useState(null);
  const [autoDateFrom, setAutoDateFrom] = useState("");
  const [autoDateTo, setAutoDateTo] = useState(todayIso());
  const [imports, setImports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    apiFetch("/environment").then(setEnvironment).catch(() => {});
    apiFetch("/results/imports/last-mediris-date")
      .then((res) => setAutoDateFrom(res.last_date ? addDaysIso(res.last_date, 1) : ""))
      .catch(() => {});
    fetchHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const fetchHistory = () => {
    setLoadingHistory(true);
    apiFetch("/results/imports")
      .then((data) => setImports(data.slice(0, 10)))
      .catch((err) => setErrorModal({ show: true, message: err.message || "Fout bij ophalen van imports." }))
      .finally(() => setLoadingHistory(false));
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      apiFetch(`/results/imports/jobs/${jobId}`)
        .then((data) => {
          setJob(data);
          if (data.status !== "running") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (data.status === "done") {
              fetchHistory();
              apiFetch("/results/imports/last-mediris-date")
                .then((res) => setAutoDateFrom(res.last_date ? addDaysIso(res.last_date, 1) : ""))
                .catch(() => {});
              if (onImported) onImported();
            }
          }
        })
        .catch((err) => {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setErrorModal({ show: true, message: err.message || "Fout bij opvolgen van import." });
        });
    }, POLL_INTERVAL_MS);
  };

  const startUpload = async (file) => {
    if (!file) return;
    setJob({ status: "running", stage: "Bestand versturen...", current: 0, total: 0 });
    try {
      const { job_id } = await uploadFile("/results/imports/upload", file);
      pollJob(job_id);
    } catch (err) {
      setJob(null);
      setErrorModal({ show: true, message: err.message || "Fout bij uploaden." });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startAutoExport = async () => {
    if (!autoDateFrom || !autoDateTo) {
      setErrorModal({ show: true, message: "Kies een start- en einddatum." });
      return;
    }
    setJob({ status: "running", stage: "Mediris-export starten (browservenster opent)...", current: 0, total: 0 });
    try {
      const { job_id } = await apiFetch("/results/imports/mediris-auto", {
        method: "POST",
        body: JSON.stringify({ date_from: autoDateFrom, date_to: autoDateTo }),
      });
      pollJob(job_id);
    } catch (err) {
      setJob(null);
      setErrorModal({ show: true, message: err.message || "Fout bij starten van Mediris-export." });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) startUpload(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) startUpload(file);
  };

  const handleDelete = async (importId) => {
    if (!window.confirm("Import en alle bijhorende consultaties verwijderen?")) return;
    try {
      await apiFetch(`/results/imports/${importId}`, { method: "DELETE" });
      fetchHistory();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen." });
    }
  };

  const busy = job?.status === "running";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Importeer Mediris data</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-5">
          {[{ key: "import", label: "Importeren" }, { key: "history", label: "Geschiedenis" }].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "import" && (
          <div className="p-5">
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => !busy && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                dragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
              } ${busy ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <p className="text-sm text-gray-600">
                Sleep een Mediris Excel-export hierheen, of klik om te bladeren.
              </p>
              <p className="text-xs text-gray-400 mt-1">Import start onmiddellijk na het droppen.</p>
            </div>

            {environment === "local" && (
              <div className="mt-5 border-t border-gray-200 pt-5">
                <div className="text-sm font-semibold text-gray-700 mb-2">
                  Automatisch ophalen uit Mediris
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Van</label>
                    <input
                      type="date"
                      value={autoDateFrom}
                      onChange={(e) => setAutoDateFrom(e.target.value)}
                      disabled={busy}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tot</label>
                    <input
                      type="date"
                      value={autoDateTo}
                      onChange={(e) => setAutoDateTo(e.target.value)}
                      disabled={busy}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={startAutoExport}
                    disabled={busy}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Ophalen &amp; importeren
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enkel beschikbaar op deze lokale omgeving — opent een zichtbaar Chrome-venster.
                </p>
              </div>
            )}

            {job && <JobProgress job={job} />}
            {job?.status === "error" && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {job.error}
              </div>
            )}
            {job?.status === "done" && <ImportResultSummary result={job.result} />}
          </div>
        )}

        {tab === "history" && (
          <div className="p-5">
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Bestand</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Datum</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Rijen</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Periode</th>
                    {canManage && <th className="px-3 py-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {imports.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 5 : 4} className="px-3 py-8 text-center text-gray-400">
                        Nog geen imports.
                      </td>
                    </tr>
                  ) : (
                    imports.map((imp, idx) => (
                      <tr key={imp.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-3 py-2 text-gray-700 max-w-xs truncate">{imp.filename}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-xs">
                          {formatDateTime(imp.imported_at)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{imp.row_count ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-xs">
                          {imp.period_from ? `${imp.period_from} — ${imp.period_to}` : "—"}
                        </td>
                        {canManage && (
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleDelete(imp.id)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Verwijderen
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
